'use client';

import React from 'react';
import classNames from 'classnames';
import { NoFirstRender } from '@benzinga/hooks';
import { isDesktop as isDesktopViewport, isMobile as isMobileViewport } from '@benzinga/device-utils';
import {
  type RaptiveAdPlacementType,
  raptiveAdWrapperClassnames,
  raptiveAdPlaceholderHeightMap,
  raptiveAdManager,
} from '@benzinga/ads-utils';
import { useBenzingaEdge } from '@benzinga/edge';
import { ErrorBoundary } from '@benzinga/core-ui';
import { useRaptiveAdImpression } from '../../hooks/useRaptiveAdImpression';

export interface RaptiveAdPlaceholderProps {
  className?: string;
  onlyClient?: boolean;
  onlyDesktop?: boolean;
  onlyMobile?: boolean;
  type: RaptiveAdPlacementType;
  minimumHeightEnabled?: boolean;
  disableMargins?: boolean;
  noWrapper?: boolean;
  adLightHidden?: boolean;
}

// Add ?ad_test=benzinga-placeholders to test the ad placements

export const RaptiveAdPlaceholder: React.FC<RaptiveAdPlaceholderProps> = ({
  className: propsClassName,
  disableMargins = false,
  minimumHeightEnabled = true,
  noWrapper = false,
  onlyClient,
  onlyDesktop,
  onlyMobile,
  type,
}) => {
  const isDisabled = !!useBenzingaEdge().disabled.displayAd;

  if (isDisabled) {
    return <div />;
  }

  const heightClassnames =
    minimumHeightEnabled && !!raptiveAdPlaceholderHeightMap[type] ? `${raptiveAdPlaceholderHeightMap[type]}` : '';

  const isMobileClassName = 'flex md:hidden' + (disableMargins ? '' : ' my-2 md:my-0');
  const isDesktopClassName = 'hidden md:flex' + (disableMargins ? '' : ' md:my-2');
  const deviceBasedClassnames = onlyMobile ? isMobileClassName : onlyDesktop ? isDesktopClassName : '';

  return (
    <NoFirstRender
      fallback={
        <div
          className={classNames(
            'min-w-[300px] w-[300px]',
            heightClassnames,
            'raptive-ad-placement mx-auto overflow-hidden',
            deviceBasedClassnames,
            propsClassName,
          )}
        />
      }
    >
      <RaptiveAdPlaceholderInner
        className={propsClassName}
        disableMargins={disableMargins}
        minimumHeightEnabled={minimumHeightEnabled}
        noWrapper={noWrapper}
        onlyClient={onlyClient}
        onlyDesktop={onlyDesktop}
        onlyMobile={onlyMobile}
        type={type}
      />
    </NoFirstRender>
  );
};

const RaptiveAdPlaceholderInner: React.FC<RaptiveAdPlaceholderProps> = ({
  className: propsClassName,
  disableMargins = false,
  minimumHeightEnabled = true,
  noWrapper = false,
  onlyClient,
  onlyDesktop,
  onlyMobile,
  type,
}) => {
  const [isMobile, setIsMobile] = React.useState<boolean>(false);
  const [isDesktop, setIsDesktop] = React.useState<boolean>(false);
  const [isClient, setIsClient] = React.useState<boolean>(false);

  const adElementRef = React.useRef<HTMLDivElement | null>(null);

  const isDisabled = !!useBenzingaEdge().disabled.displayAd;

  const requestedAdIdRef = React.useRef<string | null>(null);

  const setAdElementRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      if (!element) {
        adElementRef.current = null;
        return;
      }

      if (requestedAdIdRef.current) return;

      adElementRef.current = element;

      if (element.id && !requestedAdIdRef.current) {
        requestedAdIdRef.current = element.id;
      }

      const isMobile = isMobileViewport();
      const isDesktop = isDesktopViewport();

      const shouldRegister =
        onlyDesktop || onlyMobile
          ? (onlyDesktop && isDesktop) || (onlyMobile && isMobile)
          : !onlyDesktop && !onlyMobile;

      if (!shouldRegister) return;

      if (isDisabled || raptiveAdManager.isDisabled()) return;

      const requestedAd = raptiveAdManager.registerPlaceholder(type);

      if (requestedAd) {
        element.id = requestedAd;
        requestedAdIdRef.current = requestedAd;
      }
    },
    [onlyDesktop, onlyMobile, type, isDisabled],
  );

  React.useLayoutEffect(() => {
    const isMobile = isMobileViewport();
    const isDesktop = isDesktopViewport();

    setIsMobile(isMobile);
    setIsDesktop(isDesktop);
    setIsClient(true);

    return () => {
      if (requestedAdIdRef.current) {
        raptiveAdManager.unregisterPlaceholder(requestedAdIdRef.current);
        requestedAdIdRef.current = null;
      }
    };
  }, []);

  const isMobileClassName = 'flex md:hidden' + (disableMargins ? '' : ' my-2 md:my-0');
  const isDesktopClassName = 'hidden md:flex' + (disableMargins ? '' : ' md:my-2');
  const deviceBasedClassnames = onlyMobile ? isMobileClassName : onlyDesktop ? isDesktopClassName : '';

  const heightClassnames =
    minimumHeightEnabled && !!raptiveAdPlaceholderHeightMap[type] ? `${raptiveAdPlaceholderHeightMap[type]}` : '';

  const wrapperClassName = classNames('raptive-ad-placement mx-auto overflow-hidden', {
    [`${deviceBasedClassnames}`]: deviceBasedClassnames,
    [`${heightClassnames}`]: heightClassnames,
    [`${propsClassName}`]: propsClassName,
  });

  const adPlacementClassName = raptiveAdWrapperClassnames[type];

  const canRenderMobile = onlyMobile && isMobile;
  const canRenderDesktop = onlyDesktop && isDesktop;
  const hasNoDeviceRestrictions = !onlyMobile && !onlyDesktop;
  const deviceType = onlyDesktop ? 'desktop' : onlyMobile ? 'mobile' : undefined;

  const adElementClassName = classNames(
    adPlacementClassName,
    'w-full flex items-center justify-center',
    noWrapper ? wrapperClassName : '',
  );

  const adElement: React.ReactElement | null = adPlacementClassName ? (
    <div className={adElementClassName} data-placement-type={type} ref={setAdElementRef} />
  ) : null;

  let element: React.ReactElement | null = null;

  // if (isDisabled) {
  //   return <div />;
  // }

  const renderAdContent = () => {
    if (noWrapper) {
      return adElement;
    }
    if (!isClient && onlyMobile && !isMobile)
      return (
        <div
          className={classNames(
            'min-w-[300px] w-[300px] flex items-center justify-center',
            adPlacementClassName,
            deviceBasedClassnames,
            heightClassnames,
            propsClassName,
          )}
        />
      );
    if (!isClient && onlyDesktop && !isDesktop) {
      return (
        <div
          className={classNames(
            'min-w-[300px] w-[300px] flex items-center justify-center',
            adPlacementClassName,
            deviceBasedClassnames,
            heightClassnames,
            propsClassName,
          )}
        />
      );
    }

    return (
      <Container
        adContainerId={requestedAdIdRef.current}
        className={wrapperClassName}
        deviceType={deviceType}
        isDisabled={isDisabled}
        placementType={type}
      >
        {adElement}
      </Container>
    );
  };

  if (isClient && ((onlyMobile && isDesktop) || (onlyDesktop && isMobile))) {
    return null;
  }

  if (onlyMobile || onlyDesktop) {
    return renderAdContent();
  } else if (onlyClient) {
    if (canRenderMobile || canRenderDesktop) {
      element = renderAdContent();
    }

    return hasNoDeviceRestrictions ? renderAdContent() : element;
  } else {
    return renderAdContent();
  }
};

const Container: React.FC<
  React.PropsWithChildren<{
    className?: string;
    isDisabled: boolean;
    deviceType?: 'desktop' | 'mobile';
    placementType: RaptiveAdPlacementType;
    adContainerId: string | null;
  }>
> = ({ adContainerId, children, className, deviceType, isDisabled, placementType }) => {
  const { containerRef } = useRaptiveAdImpression({ adContainerId, placementType });

  if (isDisabled) return null;
  return (
    <ErrorBoundary name="raptive-ad-placeholder">
      <div
        className={`${className} raptive-ad-wrapper flex flex-col items-center justify-center`}
        data-device-type={deviceType}
        ref={containerRef}
      >
        {children}
      </div>
    </ErrorBoundary>
  );
};

// const StyledContainer = styled.div`
//   background-color: red;
// `;
